/** What changed between two versions of a legal document (item 113):
 *  the plain-language summary first, then the changed clauses with
 *  removed text struck through and added text underlined. Shared by the
 *  ``/legal/:doc/changes`` page and the dialog the org prompt opens.
 *
 *  Accessibility: changes are ``<del>`` / ``<ins>`` with visually hidden
 *  start and end markers, and every clause names its status in words,
 *  so nothing depends on colour. */

import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { httpStatusOf } from "@/api/connectivity";
import { useLegalChanges } from "@/api/legal";
import {
  legalChangesPath,
  legalDocLabel,
  legalLangFor,
  legalPath,
  type LegalChangedClause,
  type LegalChanges,
  type LegalDiffSegment,
  type LegalDoc,
  type LegalLang,
} from "@/features/legal/docs";
import { formatCalendarDay } from "@/i18n/format";

function clauseStatus(t: TFunction, status: LegalChangedClause["status"]): string {
  switch (status) {
    case "added":
      return t("legal.changes.status.added");
    case "removed":
      return t("legal.changes.status.removed");
    case "changed":
      return t("legal.changes.status.changed");
  }
}

/** The clause's name: "18.6" or the heading, with the section for a
 *  numbered paragraph. */
function clauseTitle(t: TFunction, clause: LegalChangedClause): string {
  if (clause.kind === "columns") {
    return t("legal.changes.columns_in_section", { section: clause.section });
  }
  if (clause.kind === "row") {
    return clause.section
      ? t("legal.changes.row_in_section", { row: clause.label, section: clause.section })
      : clause.label;
  }
  if (clause.number && clause.label === clause.number && clause.section) {
    return t("legal.changes.clause_in_section", { clause: clause.number, section: clause.section });
  }
  if (clause.number && clause.label === clause.number) {
    return clause.number;
  }
  return clause.label;
}

const delSx = {
  textDecoration: "line-through",
  textDecorationThickness: "2px",
  bgcolor: "rgba(211, 47, 47, 0.12)",
  color: "text.primary",
  borderRadius: 0.5,
} as const;

const insSx = {
  textDecoration: "underline",
  textDecorationThickness: "2px",
  bgcolor: "rgba(46, 125, 50, 0.14)",
  color: "text.primary",
  borderRadius: 0.5,
} as const;

/** A segment's text with its inline formatting. */
function Formatted({ segment }: { segment: LegalDiffSegment }): React.JSX.Element {
  let node: React.ReactNode = segment.text;
  const marks = segment.marks ?? [];
  if (marks.includes("code")) node = <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.92em" }}>{node}</Box>;
  if (marks.includes("em")) node = <em>{node}</em>;
  if (marks.includes("strong")) node = <strong>{node}</strong>;
  if (segment.href) {
    const external = /^https?:/.test(segment.href);
    node = (
      <Link href={segment.href} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
        {node}
      </Link>
    );
  }
  return <>{node}</>;
}

/** One segment: plain, ``<del>`` or ``<ins>`` with screen-reader markers. */
function Segment({ segment }: { segment: LegalDiffSegment }): React.JSX.Element {
  const { t } = useTranslation();
  if (segment.op === "same") return <Formatted segment={segment} />;
  const removed = segment.op === "removed";
  return (
    <Box component={removed ? "del" : "ins"} sx={removed ? delSx : insSx}>
      <Box component="span" sx={visuallyHidden}>
        {removed ? t("legal.changes.sr.removed_start") : t("legal.changes.sr.added_start")}{" "}
      </Box>
      <Formatted segment={segment} />
      <Box component="span" sx={visuallyHidden}>
        {" "}
        {removed ? t("legal.changes.sr.removed_end") : t("legal.changes.sr.added_end")}
      </Box>
    </Box>
  );
}

function ClauseCard({ clause }: { clause: LegalChangedClause }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Box
      component="li"
      data-testid="legal-change-clause"
      data-status={clause.status}
      sx={{
        listStyle: "none",
        borderLeft: 3,
        borderColor:
          clause.status === "added"
            ? "success.main"
            : clause.status === "removed"
              ? "error.main"
              : "warning.main",
        pl: 1.5,
        py: 0.5,
      }}
    >
      <Stack direction="row" sx={{ gap: 1, alignItems: "baseline", flexWrap: "wrap", mb: 0.5 }}>
        <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
          {clauseTitle(t, clause)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
          {clauseStatus(t, clause.status)}
        </Typography>
        {clause.previous_number && (
          <Typography variant="caption" color="text.secondary">
            {t("legal.changes.previously", { clause: clause.previous_number })}
          </Typography>
        )}
      </Stack>
      <Typography
        component="div"
        variant="body2"
        sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.7 }}
      >
        {clause.segments.map((segment, i) => (
          <Segment key={i} segment={segment} />
        ))}
      </Typography>
    </Box>
  );
}

/** The summary, the other changes and the changed clauses. */
export function LegalChangesBody({ data }: { data: LegalChanges }): React.JSX.Element {
  const { t } = useTranslation();
  const several = data.versions.length > 1;
  const hasSummary = data.versions.some((v) => v.summary.length > 0 || v.items.length > 0);
  return (
    <Stack sx={{ gap: 3 }} lang={data.lang}>
      <Typography variant="body2" color="text.secondary" data-testid="legal-changes-range">
        {t("legal.changes.range", {
          from: data.from.version,
          to: data.to.version,
          fromDate: formatCalendarDay(data.from.effective),
          toDate: formatCalendarDay(data.to.effective),
        })}
      </Typography>

      {hasSummary && (
        <Box component="section" aria-labelledby="legal-changes-summary">
          <Typography id="legal-changes-summary" variant="h6" component="h2" sx={{ mb: 1 }}>
            {t("legal.changes.summary_title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }} data-testid="legal-changes-disclaimer">
            {t("legal.changes.disclaimer")}
          </Typography>
          <Stack sx={{ gap: 1.5 }}>
            {data.versions.map((v) =>
              v.summary.length + v.items.length === 0 ? null : (
                <Box key={v.version}>
                  {several && (
                    <Typography variant="subtitle2" component="h3">
                      {t("legal.changes.in_version", {
                        version: v.version,
                        date: formatCalendarDay(v.effective),
                      })}
                    </Typography>
                  )}
                  <Box component="ul" sx={{ m: 0, pl: 3, "& li": { mb: 0.5, lineHeight: 1.6 } }}>
                    {[...v.summary, ...v.items].map((line, i) => (
                      <Typography component="li" variant="body2" key={i}>
                        {line}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ),
            )}
          </Stack>
        </Box>
      )}

      <Box component="section" aria-labelledby="legal-changes-clauses">
        <Typography id="legal-changes-clauses" variant="h6" component="h2" sx={{ mb: 0.5 }}>
          {t("legal.changes.clauses_title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("legal.changes.counts", { count: data.clauses.length })}{" "}
          {t("legal.changes.unchanged", { count: data.unchanged })}
          {data.renumbered > 0 && <> {t("legal.changes.renumbered", { count: data.renumbered })}</>}
        </Typography>
        {data.clauses.length === 0 ? (
          <Typography variant="body2">{t("legal.changes.no_text_changes")}</Typography>
        ) : (
          <Stack component="ol" sx={{ m: 0, p: 0, gap: 2 }} aria-label={t("legal.changes.clauses_title")}>
            {data.clauses.map((clause, i) => (
              <ClauseCard key={`${clause.status}-${clause.label}-${i}`} clause={clause} />
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

/** Loading and error states around :func:`LegalChangesBody`. */
export function LegalChangesView({
  doc,
  from,
  to,
  lang,
}: {
  doc: LegalDoc;
  from: number | null;
  to: number | null;
  lang: LegalLang;
}): React.JSX.Element {
  const { t } = useTranslation();
  const changes = useLegalChanges(doc, from, to, lang);
  if (changes.isLoading) return <LinearProgress />;
  if (changes.isError || !changes.data) {
    const status = httpStatusOf(changes.error);
    return (
      <Alert severity={status === 404 || status === 422 ? "info" : "error"}>
        {status === 422
          ? t("legal.changes.nothing_to_compare")
          : status === 404
            ? t("legal.changes.not_found")
            : t("legal.load_failed")}
      </Alert>
    );
  }
  return <LegalChangesBody data={changes.data} />;
}

/** The dialog variant: the org prompt and gate open it over themselves. */
export function LegalChangesDialog({
  doc,
  from,
  to,
  open,
  onClose,
}: {
  doc: LegalDoc;
  from: number | null;
  to: number | null;
  open: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  // A phone gets the whole screen: the clauses need the width.
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const lang = legalLangFor(i18n.resolvedLanguage ?? i18n.language);
  const titleId = `legal-changes-dialog-${doc}`;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      scroll="paper"
    >
      <DialogTitle id={titleId}>
        {t("legal.changes.title", { doc: legalDocLabel(t, doc) })}
      </DialogTitle>
      <DialogContent dividers>
        {open && <LegalChangesView doc={doc} from={from} to={to} lang={lang} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, gap: 1, flexWrap: "wrap" }}>
        <Link href={legalChangesPath(doc, from, to)} target="_blank" rel="noopener" sx={{ mr: "auto" }}>
          {t("legal.changes.open_page")}
        </Link>
        {to && (
          <Link href={`${legalPath(doc)}?version=${to}`} target="_blank" rel="noopener">
            {t("legal.org.version_link", { doc: legalDocLabel(t, doc), version: to })}
          </Link>
        )}
        <Button onClick={onClose}>{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

/** A "What changed in <doc>" button that opens :func:`LegalChangesDialog`. */
export function LegalChangesButton({
  doc,
  from,
  to,
  compact = false,
}: {
  doc: LegalDoc;
  from: number | null;
  to: number | null;
  /** Next to the document's own link: "Read what changed" on screen,
   *  the document's name kept for screen readers. */
  compact?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const full = t("legal.changes.what_changed_in", { doc: legalDocLabel(t, doc) });
  return (
    <>
      <Button
        size="small"
        variant="text"
        onClick={() => setOpen(true)}
        sx={{ alignSelf: "flex-start", px: 0.5, textAlign: "left", justifyContent: "flex-start" }}
        aria-haspopup="dialog"
        aria-label={compact ? full : undefined}
      >
        {compact ? t("legal.update.read") : full}
      </Button>
      <LegalChangesDialog doc={doc} from={from} to={to} open={open} onClose={() => setOpen(false)} />
    </>
  );
}
